const BRAND_HI_TRANSFORM_TRANSITION =
  'transform 0.45s cubic-bezier(0.25, 0.1, 0.25, 1)';
const BRAND_HI_HOVER_PAUSE_MS = 250;

function _bhDebounce(fn, ms) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

class BrandHighlightsSection {
    constructor(section) {
      this.section = section;
      this._introMasterMap = new Map();
      this.sliderContainer = section.querySelector('[data-brand-slider]');
      this.track = section.querySelector('.brand-highlights__slides');
      this.slides = Array.from(section.querySelectorAll('.brand-highlights__slide'));
      this.prevButton = section.querySelector('[data-brand-prev]');
      this.nextButton = section.querySelector('[data-brand-next]');
      this.dots = Array.from(section.querySelectorAll('[data-brand-dot]'));
      this.autoplaySetting = section.dataset.autoplay === 'true';
      this.autoplayInterval = Number(section.dataset.autoplayInterval) || 5000;
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragged = 0;
      // Touch gesture state (used to avoid blocking vertical page scroll on mobile)
      this._touchTracking = false;
      this._touchAxis = null; // 'x' | 'y' | null (not decided yet)
      this._touchStartX = 0;
      this._touchStartY = 0;
      this._touchTotalX = 0; // accumulate horizontal drag distance for threshold logic
      this.clickedInteractiveElement = null; // 记录点击的交互元素
      this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0; // 检测触摸设备
      // 惯性滚动相关
      this.velocity = 0; // 当前速度 (px/ms)
      this.lastMoveTime = 0; // 上一次移动的时间戳
      this.lastMoveX = 0; // 上一次移动的 X 坐标
      this.velocityHistory = []; // 速度历史记录（用于平滑计算）
      this.isMomentumScrolling = false; // 是否正在进行惯性滚动
      this.momentumRaf = null; // 惯性滚动的 RAF ID
      this.mediaQuery = window.matchMedia('(max-width: 989px)');
      this.pointerFineQuery = window.matchMedia('(hover: hover)');
      this.originalCount = this.slides.length;
      this._transitioning = false;
      this.offsets = [];
      this.trackX = 0; // in pixels
      this.autoSpeed = 0.6; // px per frame for continuous autoplay
      this.batchSize = 6;
      this.baseTemplates = [];
      this._cyclePointer = 0;
      this.virtualX = 0; // 虚拟位移（像素），用于取模渲染无缝循环
      this.virtualP = 0; // 虚拟位移（百分比，基于轨道宽度），用于百分比渲染
      this.baseOffsets = []; // 原始一套内容的偏移与宽度
      this.baseTotalWidth = 0; // 原始一套内容的总宽度（含间距）
      this.lastFrameTs = 0; // 连续动画的上一帧时间戳
      this.autoPxPerSec = 28; // 自动播放速度（像素/秒），更平滑且与设备无关
  
      // 介绍模块子轮播注册表：用于在克隆/删除时初始化与清理
      this._introGalleryRegistry = new Map();
      // 运行时尺寸缓存，避免逐帧读取布局导致回流
      this.cachedTrackWidth = 0;
      this.cachedViewportWidth = 0;
      this.basePercent = 0; // 基础周期百分比缓存
      this.lastTrackWidth = 0; // 上一次轨道宽度，用于重映射
      this._transformTransitionCleanup = null;
      this._hoverPauseTimer = null;
      this._visibilityObserver = null;

      // 缓存 prefersReducedMotion 结果，避免每次调用都新建 matchMedia 对象
      this._reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._prefersReducedMotion = this._reducedMotionQuery.matches;
      this._onReducedMotionChange = (e) => { this._prefersReducedMotion = e.matches; };
      this._reducedMotionQuery.addEventListener('change', this._onReducedMotionChange);
  
      // handleResize 加 150ms 防抖，避免窗口缩放时连续触发重排
      this.handleResize = _bhDebounce(this.handleResize.bind(this), 150);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.resumeAutoplay = this.resumeAutoplay.bind(this);
      this.pauseAutoplay = this.pauseAutoplay.bind(this);
      this._onSliderMouseEnter = this._onSliderMouseEnter.bind(this);
      this._onSliderMouseLeave = this._onSliderMouseLeave.bind(this);
      this.init();
    }

    _onSliderMouseEnter() {
      if (this._hoverPauseTimer) clearTimeout(this._hoverPauseTimer);
      this._hoverPauseTimer = window.setTimeout(() => {
        this._hoverPauseTimer = null;
        this.pauseAutoplay();
      }, BRAND_HI_HOVER_PAUSE_MS);
    }

    _onSliderMouseLeave() {
      if (this._hoverPauseTimer) {
        clearTimeout(this._hoverPauseTimer);
        this._hoverPauseTimer = null;
      }
      this.resumeAutoplay();
    }
  
    init() {
      if (!this.sliderContainer || !this.track || this.slides.length === 0) {
        return;
      }
  
      this.setupMainSlider();
      this.setupIntroductionModule();
      this.setupSupplierLogos();
  
      window.addEventListener('resize', this.handleResize);
      if (typeof this.mediaQuery.addEventListener === 'function') {
        this.mediaQuery.addEventListener('change', this.handleResize);
      }

      // section 滚出视口时暂停 RAF 循环，滚回来时恢复
      this._visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(e => {
            if (e.isIntersecting) {
              this.resumeAutoplay();
            } else {
              this.pauseAutoplay();
            }
          });
        },
        { threshold: 0 }
      );
      this._visibilityObserver.observe(this.section);
    }
  
    setupMainSlider() {
      // 改造：初始化为连续滚动（动态克隆 + 删除），无需回弹
      this.bootstrapTickerClones();
      this.computeOffsets();
      this.computeBaseMetrics();
      // 初始化尺寸缓存
      this.updateRuntimeDimensions();
  
      // 初始化虚拟位移到"中间套"，确保左右都有缓冲空间
      const total = this.baseTotalWidth || 0;
      const trackWidth = this.getTrackWidth();
      if (total > 0 && trackWidth > 0) {
        // 将初始位置设置为第二套（中间套）的开始位置
        this.virtualP = (total / trackWidth) * 100;
      }
  
      // GPU 合成层与性能提示
      if (this.track) {
        this.track.style.willChange = 'transform';
        // 应用初始 transform
        this.applyTransformPercent();
        // 设置拖动光标
        this.track.style.cursor = 'grab';
  
        // 阻止所有图片和链接的默认拖拽行为
        this.disableDragOnElement(this.track);
      }
  
      // 等待轨道中的媒体资源解码完成后再二次测量，避免初始宽度偏小导致缓冲不足
      const images = this.track ? Array.from(this.track.querySelectorAll('img')) : [];
      if (images.length) {
        Promise.all(images.map((img) => (img.decode ? img.decode().catch(() => {}) : Promise.resolve())))
          .then(() => {
            // 图片解码后尺寸变化较大，需再次确保轨道长度足够
            this.ensureSufficientWidth();
            this.computeBaseMetrics();
            const newTrack = this.cachedTrackWidth || this.getTrackWidth();
            const newTotal = this.baseTotalWidth || 0;
  
            // 重新计算并确保仍在"中间套"
            if (newTotal > 0 && newTrack > 0) {
              this.virtualP = (newTotal / newTrack) * 100;
            }
  
            this.clampAndRecentrePercent();
            this.applyTransformPercent();
          })
          .catch(() => {});
      }
      // 初始化阶段不强制对齐索引，避免与连续滚动产生跳变
      this.updateDots();
  
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.goTo(this.currentIndex - 1, true));
      }
  
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.goTo(this.currentIndex + 1, true));
      }
  
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const target = Number(dot.dataset.brandDot);
          if (!Number.isNaN(target)) {
            // 圆点索引对应真实套内的下标，需偏移到中间真实套
            const mapped = this.originalCount + target;
            this.goTo(mapped, true);
          }
        });
      });
  
      // 添加触摸事件支持（移动端）- 使用 passive: false 确保 preventDefault 生效
      this.track.addEventListener('touchstart', this.onTouchStart, { passive: false });
      this.track.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.track.addEventListener('touchend', this.onTouchEnd, { passive: false });
      this.track.addEventListener('touchcancel', this.onTouchEnd, { passive: false });
      
      // 保留 pointer 事件（桌面端和兼容性）
      this.track.addEventListener('pointerdown', this.onPointerDown, { passive: false });
      this.track.addEventListener('pointermove', this.onPointerMove, { passive: false });
      this.track.addEventListener('pointerup', this.onPointerUp, { passive: false });
      this.track.addEventListener('pointercancel', this.onPointerUp, { passive: false });
      this.track.addEventListener('pointerleave', (event) => {
        if (this.isDragging) {
          this.onPointerUp(event);
        }
      });
  
      // Unify behavior across devices: transform-based dragging for both PC/mobile
  
      if (this.pointerFineQuery.matches) {
        this.sliderContainer.addEventListener('mouseenter', this._onSliderMouseEnter);
        this.sliderContainer.addEventListener('mouseleave', this._onSliderMouseLeave);
      }
  
      this.resumeAutoplay();
    }
  
    shouldAutoplay() {
      // Enable autoplay on both desktop and mobile when more than one real slide exists
      return this.autoplaySetting && this.originalCount > 1;
    }

    prefersReducedMotion() {
      return this._prefersReducedMotion;
    }

    _clearTransformTransitionCleanup() {
      if (typeof this._transformTransitionCleanup === 'function') {
        this._transformTransitionCleanup();
      }
      this._transformTransitionCleanup = null;
    }

    /**
     * Runs after CSS transform transition completes (or immediately if reduced motion / no transition).
     */
    _afterTransformTransition(onComplete) {
      if (!this.track || typeof onComplete !== 'function') {
        if (typeof onComplete === 'function') onComplete();
        return;
      }
      this._clearTransformTransitionCleanup();
      if (this.prefersReducedMotion()) {
        onComplete();
        return;
      }
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        this._clearTransformTransitionCleanup();
        onComplete();
      };
      const onEnd = (e) => {
        if (e.target !== this.track || e.propertyName !== 'transform') return;
        this.track.removeEventListener('transitionend', onEnd);
        clearTimeout(fallbackTimer);
        finish();
      };
      const fallbackTimer = window.setTimeout(finish, 520);
      this.track.addEventListener('transitionend', onEnd);
      this._transformTransitionCleanup = () => {
        this.track.removeEventListener('transitionend', onEnd);
        clearTimeout(fallbackTimer);
      };
    }
  
    goTo(index, userInitiated = false) {
      const realTotal = Math.max(this.originalCount, 0);
      if (!realTotal) return;
      this._clearTransformTransitionCleanup();
      const nextIndex = ((index % realTotal) + realTotal) % realTotal; // 基于原始一套的环绕索引
      this.currentIndex = nextIndex;
      this._transitioning = true;
      const animate = !this.prefersReducedMotion();
      this.jumpToIndex(nextIndex, animate);
  
      this.updateDots();
  
      if (userInitiated) {
        if (animate) {
          this._afterTransformTransition(() => this.resumeAutoplay());
        } else {
          this.resumeAutoplay();
        }
      }
    }
  
    updateDots() {
      const realTotal = Math.max(this.originalCount, 1);
      const realIndex = ((this.currentIndex % realTotal) + realTotal) % realTotal;
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === realIndex);
      });
    }
  
    updateSliderPosition() {
      // 在更新位置前刷新偏移，以确保断点变化时百分比计算准确
      this.computeOffsets();
      this.clampAndRecentrePercent();
      this.applyTransformPercent();
    }
  
    handleResize() {
      // 视口变化后，先确保轨道长度充足再更新位置
      const oldTrack = this.cachedTrackWidth || this.getTrackWidth();
      this.ensureSufficientWidth();
      const newTrack = this.cachedTrackWidth || this.getTrackWidth();
      if (oldTrack > 0 && newTrack > 0) {
        this.rebaseVirtualPercent(oldTrack, newTrack);
      }
      this.clampAndRecentrePercent();
      this.applyTransformPercent();
      this.resumeAutoplay();
    }
  
    // 更新运行时尺寸缓存：初始化、图片解码后或窗口尺寸变化时调用
    updateRuntimeDimensions() {
      this.cachedTrackWidth = this.track ? this.track.scrollWidth : 0;
      this.cachedViewportWidth = this.sliderContainer ? this.sliderContainer.clientWidth : 0;
      const total = this.baseTotalWidth || 0;
      const trackWidth = this.cachedTrackWidth || 0;
      this.basePercent = (total > 0 && trackWidth > 0) ? (total / trackWidth) * 100 : 0;
    }
  
    // 在轨道宽度变化时保持像素位置不变，平滑重映射到百分比空间
    rebaseVirtualPercent(oldTrackWidth, newTrackWidth) {
      const px = (this.virtualP / 100) * oldTrackWidth;
      this.virtualP = (px / newTrackWidth) * 100;
    }
  
    onPointerDown(event) {
      // 在触摸设备上，如果 pointer 事件是由触摸触发的，忽略它（让触摸事件处理）
      if (this.isTouchDevice && event.pointerType === 'touch') {
        return;
      }
      
      // 停止惯性滚动
      this.stopMomentum();
      this._clearTransformTransitionCleanup();
      
      // 检查点击目标是否为可交互元素（按钮、链接等）
      const target = event.target;
      const clickedInteractive = target.closest('button, a, [role="button"], input, select, textarea, [tabindex]');
      
      // 记录点击的交互元素，但不阻止拖拽启动
      this.clickedInteractiveElement = clickedInteractive || null;
      
      // 仅当非交互元素时阻止默认行为，以允许链接/按钮正常点击
      if (!clickedInteractive) {
        event.preventDefault();
      }
      
      this.isDragging = true;
      // 优化移动端坐标获取：优先使用 touches
      const currentX = event.touches?.[0]?.clientX ?? event.clientX ?? 0;
      this.dragStartX = currentX;
      this.lastMoveX = currentX;
      this.lastMoveTime = performance.now();
      this.dragged = 0;
      this.velocity = 0;
      this.velocityHistory = [];
      if (!clickedInteractive) {
        this.track.setPointerCapture?.(event.pointerId);
      }
      this.track.style.cursor = 'grabbing';
      this.pauseAutoplay();
    }
  
    onPointerMove(event) {
      if (!this.isDragging) return;
      
      // 在触摸设备上，如果 pointer 事件是由触摸触发的，忽略它（让触摸事件处理）
      if (this.isTouchDevice && event.pointerType === 'touch') {
        return;
      }
  
      // 移除对交互元素的检查，允许在链接/按钮上拖拽
  
      const currentX = event.touches?.[0]?.clientX ?? event.clientX ?? 0;
      const currentTime = performance.now();
      const delta = currentX - this.dragStartX;
      this.dragged = delta;
      
      // 计算速度（用于惯性滚动）- 与移动端保持一致
      const timeDelta = currentTime - this.lastMoveTime;
      if (timeDelta > 0 && timeDelta < 100) { // 忽略异常大的时间差
        const moveDelta = currentX - this.lastMoveX;
        const instantVelocity = moveDelta / timeDelta; // px/ms
        
        // 使用历史记录平滑速度计算
        this.velocityHistory.push(instantVelocity);
        const maxHistory = this.isTouchDevice ? 8 : 5; // 移动端使用更多历史记录
        if (this.velocityHistory.length > maxHistory) {
          this.velocityHistory.shift();
        }
        
        // 移动端使用更平滑的加权（更重视最近的速度）
        const historyLength = this.velocityHistory.length;
        let totalWeight = 0;
        this.velocity = this.velocityHistory.reduce((sum, v, i) => {
          // 移动端：更平滑的指数加权
          const weight = this.isTouchDevice 
            ? Math.pow(0.7, historyLength - 1 - i) // 指数衰减，更重视最近
            : (i < 5 ? [0.1, 0.15, 0.2, 0.25, 0.3][i] : 0.2);
          totalWeight += weight;
          return sum + v * weight;
        }, 0);
        // 归一化
        if (totalWeight > 0) {
          this.velocity = this.velocity / totalWeight;
        }
      }
      
      this.lastMoveX = currentX;
      this.lastMoveTime = currentTime;
  
      // 降低阈值到 5px，移动端更敏感
      // 立即调用 preventDefault，不等待阈值
      if (Math.abs(this.dragged) > 5) {
        event.preventDefault();
        this.pauseAutoplay();
        this.track.style.transition = 'none';
        // 拖拽仅更新虚拟位移，避免移动/克隆 DOM 引发重排
        const trackWidth = this.cachedTrackWidth || this.getTrackWidth();
        if (trackWidth > 0) {
          this.virtualP -= (delta / trackWidth) * 100;
          this.clampAndRecentrePercent();
          this.applyTransformPercent();
        }
        this.dragStartX = currentX;
      } else if (Math.abs(this.dragged) > 2) {
        // 即使移动很小也阻止默认行为，防止滚动干扰
        event.preventDefault();
      }
    }
  
    onPointerUp(event) {
      if (!this.isDragging) return;
      
      // 在触摸设备上，如果 pointer 事件是由触摸触发的，忽略它（让触摸事件处理）
      if (this.isTouchDevice && event.pointerType === 'touch') {
        return;
      }
      
      const clickThreshold = 5; // 小于这个距离视为点击而非拖拽
  
      this.track.style.cursor = 'grab';
  
      const wasDragging = Math.abs(this.dragged) >= clickThreshold;
      const clickedInteractive = this.clickedInteractiveElement;
  
      // 如果检测到是拖拽操作，阻止链接/按钮的点击
      if (wasDragging && clickedInteractive) {
        event.preventDefault();
        event.stopPropagation();
        // 阻止后续的点击事件
        if (clickedInteractive.tagName === 'A' || clickedInteractive.tagName === 'BUTTON') {
          clickedInteractive.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, { once: true, capture: true });
        }
      }
  
      // 如果移动距离很小，视为点击而非拖拽，允许链接正常点击
      if (Math.abs(this.dragged) < clickThreshold) {
        this.isDragging = false;
        this.dragged = 0;
        this.velocity = 0;
        this.velocityHistory = [];
        this.clickedInteractiveElement = null; // 清理
        this.track.releasePointerCapture?.(event?.pointerId);
        this.resumeAutoplay();
        return; // 允许链接正常点击
      }
  
      // 拖拽操作，阻止默认行为
      if (clickedInteractive) {
        event.preventDefault();
      }
      
      // 根据速度决定是惯性滚动还是直接跳转
      // 移动端使用更低的速度阈值，更容易触发惯性滚动
      const minVelocity = this.isTouchDevice ? 0.08 : 0.15; // 移动端阈值更低 (px/ms)
      const absVelocity = Math.abs(this.velocity);
      
      if (absVelocity > minVelocity) {
        // 使用惯性滚动 - 移动端增强速度
        const enhancedVelocity = this.isTouchDevice ? this.velocity * 1.2 : this.velocity; // 移动端稍微增强速度
        this.startMomentum(enhancedVelocity);
      } else {
        // 停在当前位移，不吸附卡片；仅同步圆点索引
        this.syncCurrentIndexFromVirtual();
        this.resumeAutoplay();
      }
  
      this.isDragging = false;
      this.dragged = 0;
      this.velocity = 0;
      this.velocityHistory = [];
      this.clickedInteractiveElement = null; // 清理
      this.track.releasePointerCapture?.(event?.pointerId);
    }
  
    onTouchStart(event) {
      if (!event.touches || !event.touches.length) return;

      // 停止惯性滚动
      this.stopMomentum();
      this._clearTransformTransitionCleanup();

      // Track potential drag but do NOT block default behavior yet.
      // We'll decide axis on first meaningful move: vertical scroll should remain native/smooth.
      const target = event.target;
      const clickedInteractive = target.closest('button, a, [role="button"], input, select, textarea, [tabindex]');
      this.clickedInteractiveElement = clickedInteractive || null;

      const t = event.touches[0];
      this._touchTracking = true;
      this._touchAxis = null;
      this._touchStartX = t.clientX;
      this._touchStartY = t.clientY;
      this._touchTotalX = 0;

      // Do not mark as dragging until axis locks to X
      this.isDragging = false;
      this.dragStartX = t.clientX;
      this.lastMoveX = t.clientX;
      this.lastMoveTime = performance.now();
      this.dragged = 0;
      this.velocity = 0;
      this.velocityHistory = [];
    }
  
    onTouchMove(event) {
      if (!this._touchTracking || !event.touches || !event.touches.length) return;

      const t = event.touches[0];
      const currentX = t.clientX;
      const currentY = t.clientY;

      // Decide gesture axis once movement is meaningful
      if (!this._touchAxis) {
        const dx = currentX - this._touchStartX;
        const dy = currentY - this._touchStartY;
        const ax = Math.abs(dx);
        const ay = Math.abs(dy);
        const intentThreshold = 6;

        if (ax < intentThreshold && ay < intentThreshold) {
          return; // don't block scroll/taps yet
        }

        if (ay > ax) {
          // Vertical scroll intent: let the browser handle it.
          this._touchAxis = 'y';
          this._touchTracking = false;
          this.isDragging = false;
          this.dragged = 0;
          this.clickedInteractiveElement = null;
          return;
        }

        // Horizontal intent: lock and start custom dragging
        this._touchAxis = 'x';
        this.isDragging = true;
        this.track.style.cursor = 'grabbing';
        this.pauseAutoplay();
      }

      if (this._touchAxis !== 'x' || !this.isDragging) return;

      // Horizontal drag: prevent page scroll and apply transform.
      event.preventDefault();

      const currentTime = performance.now();
      const delta = currentX - this.dragStartX; // incremental delta
      this._touchTotalX += delta;
      this.dragged = this._touchTotalX;
      
      // 计算速度（用于惯性滚动）
      const timeDelta = currentTime - this.lastMoveTime;
      if (timeDelta > 0) {
        const moveDelta = currentX - this.lastMoveX;
        const instantVelocity = moveDelta / timeDelta; // px/ms
        
        // 使用历史记录平滑速度计算
        this.velocityHistory.push(instantVelocity);
        if (this.velocityHistory.length > 5) {
          this.velocityHistory.shift();
        }
        // 使用加权平均计算平滑速度
        const weights = [0.1, 0.15, 0.2, 0.25, 0.3];
        this.velocity = this.velocityHistory.reduce((sum, v, i) => {
          const weight = weights[this.velocityHistory.length - 1 - i] || 0.2;
          return sum + v * weight;
        }, 0);
      }
      
      this.lastMoveX = currentX;
      this.lastMoveTime = currentTime;

      if (Math.abs(this.dragged) > 2) {
        this.track.style.transition = 'none';
        const trackWidth = this.cachedTrackWidth || this.getTrackWidth();
        if (trackWidth > 0) {
          // 移动端使用更平滑的位移更新
          this.virtualP -= (delta / trackWidth) * 100;
          this.clampAndRecentrePercent();
          this.applyTransformPercent();
        }
      }

      this.dragStartX = currentX;
    }
  
    onTouchEnd(event) {
      if (!this._touchTracking && !this.isDragging) return;

      // If we never locked to horizontal, don't interfere with default behavior.
      if (this._touchAxis !== 'x' || !this.isDragging) {
        this._touchTracking = false;
        this._touchAxis = null;
        this.isDragging = false;
        this.dragged = 0;
        this._touchTotalX = 0;
        this.clickedInteractiveElement = null;
        return;
      }

      const clickThreshold = 5;
      
      this.track.style.cursor = 'grab';
      
      const wasDragging = Math.abs(this.dragged) >= clickThreshold;
      const clickedInteractive = this.clickedInteractiveElement;
      
      if (wasDragging && clickedInteractive) {
        event.preventDefault();
        event.stopPropagation();
        if (clickedInteractive.tagName === 'A' || clickedInteractive.tagName === 'BUTTON') {
          clickedInteractive.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, { once: true, capture: true });
        }
      }
      
      if (Math.abs(this.dragged) < clickThreshold) {
        this.isDragging = false;
        this.dragged = 0;
        this._touchTracking = false;
        this._touchAxis = null;
        this._touchTotalX = 0;
        this.clickedInteractiveElement = null;
        this.resumeAutoplay();
        return;
      }
      
      if (clickedInteractive) {
        event.preventDefault();
      }
      
      // 根据速度决定是惯性滚动还是直接跳转
      // 移动端使用更低的速度阈值，更容易触发惯性滚动
      const minVelocity = this.isTouchDevice ? 0.08 : 0.15; // 移动端阈值更低 (px/ms)
      const absVelocity = Math.abs(this.velocity);
      
      if (absVelocity > minVelocity) {
        // 使用惯性滚动 - 移动端增强速度
        const enhancedVelocity = this.isTouchDevice ? this.velocity * 1.2 : this.velocity; // 移动端稍微增强速度
        this.startMomentum(enhancedVelocity);
      } else {
        this.syncCurrentIndexFromVirtual();
        this.resumeAutoplay();
      }
      
      this.isDragging = false;
      this.dragged = 0;
      this.velocity = 0;
      this.velocityHistory = [];
      this._touchTracking = false;
      this._touchAxis = null;
      this._touchTotalX = 0;
      this.clickedInteractiveElement = null;
    }
  
    onScroll() {
      if (!this.mediaQuery.matches) return;
      window.cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = window.requestAnimationFrame(() => {
        const scrollLeft = this.track.scrollLeft;
        const width = this.track.clientWidth;
        const index = Math.round(scrollLeft / width);
        if (index !== this.currentIndex) {
          this.currentIndex = index;
          this.updateDots();
        }
      });
    }
    
    // 开始惯性滚动
    startMomentum(initialVelocity) {
      this.stopMomentum();
      this.isMomentumScrolling = true;
      this.pauseAutoplay();
      this.track.style.transition = 'none';
      
      const trackWidth = this.cachedTrackWidth || this.getTrackWidth();
      if (trackWidth <= 0) {
        this.isMomentumScrolling = false;
        this.resumeAutoplay();
        return;
      }
      
      // 将速度从 px/ms 转换为 %/ms
      const velocityPercent = (initialVelocity / trackWidth) * 100;
      let velocity = velocityPercent;
      // 移动端使用更低的摩擦系数，让滚动更持久
      const friction = this.isTouchDevice ? 0.94 : 0.92; // 移动端 94%，桌面端 92%
      const minVelocity = 0.005; // 降低最小速度阈值，让滚动更平滑
      let lastTime = performance.now();
      
      const animate = (currentTime) => {
        if (!this.isMomentumScrolling) return;
        
        const dt = Math.min(currentTime - lastTime, 16); // 限制最大时间差为 16ms，避免长时间暂停后的跳跃
        lastTime = currentTime;
        
        // 应用速度
        if (Math.abs(velocity) > minVelocity) {
          this.virtualP -= velocity * dt;
          this.clampAndRecentrePercent();
          this.applyTransformPercent();
          
          // 应用摩擦，逐渐减速
          // 移动端使用更平滑的衰减曲线
          const frameFriction = this.isTouchDevice 
            ? Math.pow(friction, dt / 16) 
            : Math.pow(friction, dt / 16);
          velocity *= frameFriction;
        } else {
          // 速度太低：停在当前位置，不吸附卡片
          this.stopMomentum();
          this.syncCurrentIndexFromVirtual();
          this.resumeAutoplay();
          return;
        }
        
        this.momentumRaf = requestAnimationFrame(animate);
      };
      
      this.momentumRaf = requestAnimationFrame(animate);
    }
    
    // 停止惯性滚动
    stopMomentum() {
      this.isMomentumScrolling = false;
      if (this.momentumRaf) {
        cancelAnimationFrame(this.momentumRaf);
        this.momentumRaf = null;
      }
    }
  
    createClones() {
      if (!this.track) return;
      const slides = Array.from(this.track.querySelectorAll('.brand-highlights__slide'));
      this.originalCount = slides.length;
      if (slides.length <= 1) return;
  
      // Remove existing clones if any
      Array.from(this.track.querySelectorAll('.brand-highlights__slide.is-clone')).forEach((el) => el.remove());
  
      const first = slides[0];
      const last = slides[slides.length - 1];
      const firstClone = first.cloneNode(true);
      const lastClone = last.cloneNode(true);
      firstClone.classList.add('is-clone');
      lastClone.classList.add('is-clone');
      this.track.appendChild(firstClone);
      this.track.insertBefore(lastClone, first);
  
      // Refresh slides collection including clones
      this.slides = Array.from(this.track.querySelectorAll('.brand-highlights__slide'));
      // Start from the first real slide (index 1)
      this.currentIndex = 1;
  
      // No CSS transition snapping; handle loop in pixel space
    }
  
    // 保证轨道总宽度充足：至少为 视口宽度 + 原始一套宽度的两倍
    ensureSufficientWidth() {
      if (!this.track) return;
      const templates = this.baseTemplates && this.baseTemplates.length
        ? this.baseTemplates
        : Array.from(this.track.querySelectorAll('.brand-highlights__slide:not(.is-clone)')).map((el) => el.cloneNode(true));
      if (!templates.length) return;

      const containerWidth = this.sliderContainer?.clientWidth || 0;
      const baseTotal = this.baseTotalWidth || 0;

      // 一次性读取轨道宽度（单次重排），然后计算需要追加几套内容
      const currentTotalWidth = this.track.scrollWidth;
      const needed = baseTotal > 0
        ? containerWidth + baseTotal * 2
        : containerWidth * 2;

      if (currentTotalWidth < needed && baseTotal > 0) {
        const setsToAdd = Math.min(Math.ceil((needed - currentTotalWidth) / baseTotal), 10);
        const fragment = document.createDocumentFragment();
        for (let s = 0; s < setsToAdd; s++) {
          templates.forEach((tpl) => {
            const clone = tpl.cloneNode(true);
            clone.classList.add('is-clone');
            fragment.appendChild(clone);
          });
        }
        // 一次性写入 DOM（单次重排）
        this.track.appendChild(fragment);
      }

      this.slides = Array.from(this.track.querySelectorAll('.brand-highlights__slide'));
      this.updateRuntimeDimensions();
    }
  
    // 初始化：确保轨道初始长度足够（至少两套内容），以避免初始空白
    bootstrapTickerClones() {
      if (!this.track) return;
      const baseSlides = Array.from(this.track.querySelectorAll('.brand-highlights__slide'));
      this.originalCount = baseSlides.length;
      if (this.originalCount <= 1) return;
  
      // 保存初始模板用于后续批量克隆（同时禁用拖拽）
      this.baseTemplates = baseSlides.map((el) => {
        const clone = el.cloneNode(true);
        this.disableDragOnElement(clone);
        return clone;
      });
  
      // 清理旧克隆
      Array.from(this.track.querySelectorAll('.brand-highlights__slide.is-clone')).forEach((el) => el.remove());
  
      // 预先计算原始一套总宽度，用于确定足够的缓冲宽度
      this.computeBaseMetrics();
      // 使用统一方法确保宽度充足，便于在解码后和 resize 时重用
      this.ensureSufficientWidth();
      this.slides = Array.from(this.track.querySelectorAll('.brand-highlights__slide'));
      this.currentIndex = 0;
    }
  
    // 禁用元素及其子元素的拖拽行为
    // 样式层面的禁用（pointer-events、user-select）由 CSS 处理，这里只设置 HTML 属性
    disableDragOnElement(element) {
      if (!element) return;
      element.querySelectorAll('img').forEach(img => { img.draggable = false; });
      element.querySelectorAll('a').forEach(link => { link.draggable = false; });
    }
  
    computeOffsets() {
      const slides = Array.from(this.track.querySelectorAll('.brand-highlights__slide'));
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      let accum = 0;
      this.offsets = slides.map((el, idx) => {
        const w = el.offsetWidth;
        const x = accum;
        accum += w + (idx < slides.length - 1 ? gap : 0);
        return { x, w };
      });
      this.updateRuntimeDimensions();
    }
  
    indexToX(index) {
      if (!this.offsets.length) return 0;
      const { x } = this.offsets[index] || this.offsets[0];
      return x;
    }
  
    jumpToIndex(index, animate = false) {
      // 将虚拟位移调整到最接近目标项的基准位置，避免视觉跳变
      this.currentIndex = index;
      const total = this.baseTotalWidth || 0;
      const trackWidth = this.getTrackWidth();
      const targetBaseX = (this.baseOffsets[index]?.x) ?? 0;
      if (total > 0 && trackWidth > 0) {
        // 对齐到中间套，使用百分比位移以避免在不同断点上的跳变
        const targetPx = total + targetBaseX;
        this.virtualP = (targetPx / trackWidth) * 100;
        this.track.style.transition = animate ? BRAND_HI_TRANSFORM_TRANSITION : 'none';
        this.clampAndRecentrePercent();
        this.applyTransformPercent();
      } else {
        // 退化为原逻辑（像素制）
        this.trackX = -this.indexToX(index);
        this.track.style.transition = animate ? BRAND_HI_TRANSFORM_TRANSITION : 'none';
        this.applyTransform();
      }
    }
  
    applyTransform() {
      this.track.style.transform = `translateX(${this.trackX}px)`;
    }
  
    applyTransformModulo() {
      const total = this.baseTotalWidth || 0;
      if (total > 0) {
        const x = ((this.virtualX % total) + total) % total;
        this.track.style.transform = `translate3d(${-x}px, 0, 0)`;
      } else {
        this.applyTransform();
      }
    }
  
    applyTransformContinuous() {
      // 线性位移渲染；通过在 autoplay 中无感重置，避免可见回弹
      this.track.style.transform = `translate3d(${-this.virtualX}px, 0, 0)`;
    }
  
    // 参考 rejouice 实现：使用百分比进行 translate3d，避免像素断点差异
    applyTransformPercent() {
      this.track.style.transform = `translate3d(${-this.virtualP}%, 0, 0)`;
    }
  
    // 保持视口内始终有内容：根据轨道总宽与视口宽度做无感重置
    clampAndRecentre() {
      const total = this.baseTotalWidth || 0;
      const viewport = this.cachedViewportWidth || 0;
      const last = this.offsets[this.offsets.length - 1];
      const trackWidth = last ? (last.x + last.w) : 0;
      if (total <= 0 || trackWidth <= 0 || viewport <= 0) return;
      // 右界限为轨道总宽-视口，为避免出现空白提前 1px 重置
      const rightLimit = Math.max(total, trackWidth - viewport - 1);
      if (this.virtualX > rightLimit) this.virtualX -= total;
      if (this.virtualX < 0) this.virtualX += total;
    }
  
    // 百分比空间的无感重置：使用纯取模，确保位移始终处于一个基准周期内（中间套）
    clampAndRecentrePercent() {
      const total = this.baseTotalWidth || 0;
      const viewport = this.cachedViewportWidth || 0;
      const basePercent = this.basePercent || 0;
      if (total <= 0 || basePercent <= 0 || viewport <= 0) return;
  
      // 取模到 [0, basePercent) 范围
      const modValue = ((this.virtualP % basePercent) + basePercent) % basePercent;
  
      // 偏移到"中间套" [basePercent, 2 * basePercent) 范围，确保左右都有缓冲
      this.virtualP = modValue + basePercent;
    }
  
    getTrackWidth() {
      // 优先使用缓存，避免在动画帧中读取布局
      if (this.cachedTrackWidth && this.cachedTrackWidth > 0) return this.cachedTrackWidth;
      const last = this.offsets[this.offsets.length - 1];
      return last ? (last.x + last.w) : 0;
    }
  
    appendBatchToEnd() {
      if (!this.track) return;
      const count = Math.min(this.batchSize, this.baseTemplates.length || this.originalCount);
      for (let i = 0; i < count; i++) {
        const template = this.baseTemplates[i] || null;
        let clone;
        if (template) {
          clone = template.cloneNode(true);
        } else {
          const basis = this.track.querySelectorAll('.brand-highlights__slide')[0];
          if (!basis) return;
          clone = basis.cloneNode(true);
        }
        clone.classList.add('is-clone');
        this.track.appendChild(clone);
        this.initGalleriesInSlide(clone);
      }
      this.computeOffsets();
    }
  
    prependBatchToStart() {
      if (!this.track) return;
      const slides = Array.from(this.track.querySelectorAll('.brand-highlights__slide'));
      if (!slides.length) return;
      const count = Math.min(this.batchSize, slides.length);
  
      // 计算将要移动的末尾 count 项总宽度（含间距）
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      let movedWidth = 0;
      for (let i = slides.length - count; i < slides.length; i++) {
        movedWidth += slides[i].offsetWidth + (i < slides.length - 1 ? gap : 0);
      }
  
      // 依序将末尾项移动到开头，保持相对顺序
      const firstNode = this.track.querySelector('.brand-highlights__slide');
      for (let i = slides.length - count; i < slides.length; i++) {
        const node = slides[i];
        this.track.insertBefore(node, firstNode);
      }
  
      // 校正位移与索引：插入到前面后，视觉不动
      this.trackX -= movedWidth;
      this.applyTransform();
      this.computeOffsets();
      this.currentIndex += count;
      this.updateDots();
    }
  
    // 连续自动播放：基于时间差将像素速度转换为百分比位移，保证不同设备与断点的平滑一致
    resumeAutoplay() {
      if (!this.autoplaySetting) return;
      this.pauseAutoplay();
      this.track.style.transition = 'none';
      // 重置时间戳确保首帧从当前时刻开始
      this.lastFrameTs = 0;
      const tick = (ts) => {
        // 拖拽时跳过位移更新但保持 RAF 循环，避免停止
        if (!this.lastFrameTs) this.lastFrameTs = ts;
        const dt = Math.max(0, Math.min(ts - this.lastFrameTs, 100)); // 限制最大时间差为100ms，避免长时间暂停后的跳跃
        this.lastFrameTs = ts;
        const trackWidth = this.cachedTrackWidth || this.getTrackWidth();
        const total = this.baseTotalWidth || 0;
        if (!this.isDragging && trackWidth > 0 && total > 0) {
          const percentPerMs = (this.autoPxPerSec / trackWidth) * 100 / 1000; // px/s -> %/ms
          this.virtualP += percentPerMs * dt;
          this.clampAndRecentrePercent();
          this.applyTransformPercent();
        }
        this._af = requestAnimationFrame(tick);
      };
      this._af = requestAnimationFrame(tick);
    }
  
    pauseAutoplay() {
      if (this._af) cancelAnimationFrame(this._af);
      this._af = null;
      // 重置时间戳，避免恢复时产生大的时间差导致跳跃
      this.lastFrameTs = 0;
      // 如果正在惯性滚动，不要停止它
      if (this.isMomentumScrolling) return;
    }
  
    // 连续滚动重平衡：当前视窗左侧完全离开的节点，克隆到末尾并删除原节点
    rebalanceForContinuous() {
      // 改为无操作：不在滚动/拖拽过程中移动或克隆 DOM
      // 保持虚拟位移 + 取模渲染的无缝效果
    }
  
    /** 根据当前 virtualP 更新 currentIndex / 圆点，不改变 transform（拖拽释放后不吸附卡片） */
    syncCurrentIndexFromVirtual() {
      if (!this.baseOffsets.length) return;
      const total = this.baseTotalWidth || 0;
      const trackWidth = this.cachedTrackWidth || this.getTrackWidth();
      const currentCycleX = (total > 0 && trackWidth > 0)
        ? (((((this.virtualP / 100) * trackWidth) % total) + total) % total)
        : 0;
      let nearest = 0;
      let minDelta = Infinity;
      for (let i = 0; i < this.baseOffsets.length; i++) {
        const dx = Math.abs((this.baseOffsets[i]?.x || 0) - currentCycleX);
        if (dx < minDelta) {
          minDelta = dx;
          nearest = i;
        }
      }
      this.currentIndex = nearest;
      this.updateDots();
    }
  
    computeBaseMetrics() {
      if (!this.track) return;
      const baseSlides = Array.from(this.track.querySelectorAll('.brand-highlights__slide:not(.is-clone)'));
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      let accum = 0;
      this.baseOffsets = baseSlides.map((el, idx) => {
        const w = el.offsetWidth;
        const x = accum;
        accum += w + (idx < baseSlides.length - 1 ? gap : 0);
        return { x, w };
      });
      this.baseTotalWidth = accum;
    }
  
    setupIntroductionModule() {
      // 支持同一 section 中多个介绍画廊；为每个画廊独立初始化和清理
      const galleries = Array.from(this.section.querySelectorAll('[data-intro-gallery]'));
      galleries.forEach((gallery) => this._initIntroGallery(gallery));
    }
  
    _initIntroGallery(introGallery) {
        if (!introGallery || this._introGalleryRegistry.has(introGallery)) return;
    
        // ensure unique id for matching clones; prefer explicit attribute if template provides one
        let introId = introGallery.dataset.introId;
        if (!introId) {
        introId = `intro-${Math.random().toString(36).slice(2,9)}`;
        introGallery.dataset.introId = introId;
        }
    
        if (this._introMasterMap.has(introId)) {
        const master = this._introMasterMap.get(introId);
        if (master && typeof master.syncToClones === 'function') {
            master.syncToClones();
        }
        this._introGalleryRegistry.set(introGallery, () => {});
        return;
        }
    
        const items = Array.from(introGallery.querySelectorAll('[data-intro-item]'));
        const bottomEl = introGallery.closest('.brand-intro__bottom') || introGallery.parentElement;
        const buttons = Array.from(bottomEl ? bottomEl.querySelectorAll('[data-intro-index]') : []);
        const titleEl = introGallery.closest('.brand-highlights__slide')?.querySelector('[data-intro-title]') || null;
    
        let current = 0;
        let introTimer = null;
        const interval = 2000;
    
        const applyStateToDom = (index) => {
        if (!items.length) return;
        items.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === index));
        buttons.forEach((btn, btnIndex) => btn.classList.toggle('is-active', btnIndex === index));
        if (titleEl) {
            const active = items[index];
            const nextTitle = active && active.dataset.introTitle ? active.dataset.introTitle : (titleEl.dataset.defaultTitle || titleEl.textContent || '');
            if (nextTitle) titleEl.textContent = nextTitle;
        }
        };
    
        const activate = (index) => {
        current = ((index % items.length) + items.length) % items.length;
        applyStateToDom(current);
        const clones = Array.from(this.track.querySelectorAll(`[data-intro-id="${introId}"]`));
        clones.forEach(cl => {
            const clItems = Array.from(cl.querySelectorAll('[data-intro-item]'));
            const clBottom = cl.closest('.brand-intro__bottom') || cl.parentElement;
            const clButtons = clBottom ? Array.from(clBottom.querySelectorAll('[data-intro-index]')) : [];
            clItems.forEach((it, idx) => it.classList.toggle('is-active', idx === current));
            clButtons.forEach((btn, idx) => btn.classList.toggle('is-active', idx === current));
            const clTitle = cl.closest('.brand-highlights__slide')?.querySelector('[data-intro-title]');
            if (clTitle) {
            const active = clItems[current];
            const nextTitle = active && active.dataset.introTitle ? active.dataset.introTitle : (clTitle.dataset.defaultTitle || clTitle.textContent || '');
            if (nextTitle) clTitle.textContent = nextTitle;
            }
        });
        };
    
        const start = () => {
        if (items.length <= 1) return;
        stop();
        introTimer = window.setInterval(() => {
            activate((current + 1) % items.length);
        }, interval);
        };
        const stop = () => {
        if (introTimer) {
            window.clearInterval(introTimer);
            introTimer = null;
        }
        };
    
        // bind controls (buttons / next) same as before...
        buttons.forEach((button) => {
        button.addEventListener('click', () => {
            const index = Number(button.dataset.introIndex);
            if (!Number.isNaN(index)) {
            activate(index);
            start();
            }
        });
        });
    
        introGallery.querySelectorAll('[data-intro-item]').forEach(it => {
        it.addEventListener('click', () => {
            activate((current + 1) % items.length);
            start();
        });
        });
    
        introGallery.addEventListener('mouseenter', stop);
        introGallery.addEventListener('mouseleave', start);
    
        // 初次激活并启动
        activate(0);
        start();
    
        // 注册 master（含一个 sync 函数供 clone 使用）
        this._introMasterMap.set(introId, {
        introGallery,
        activate,
        start,
        stop,
        syncToClones: () => {
            // 直接调用 activate(current) 会把状态写到所有 clone（activate 中已做）
            activate(current);
        }
        });
    
        // 注册清理方法
        this._introGalleryRegistry.set(introGallery, () => {
        stop();
        // 清理 master map
        if (this._introMasterMap.get(introId)?.introGallery === introGallery) {
            this._introMasterMap.delete(introId);
        }
        });
    }
  
  
    // 初始化某个幻灯片中的所有介绍画廊
    initGalleriesInSlide(slide) {
      if (!slide) return;
      if (slide.classList && slide.classList.contains('is-clone')) return;
      const galleries = Array.from(slide.querySelectorAll('[data-intro-gallery]'));
      galleries.forEach((g) => this._initIntroGallery(g));
    }
  
    // 清理某个元素下已注册的介绍画廊（用于删除原节点前）
    cleanupIntroGalleryForEl(rootEl) {
      if (!rootEl) return;
      const galleries = Array.from(rootEl.querySelectorAll('[data-intro-gallery]'));
      galleries.forEach((g) => {
        const cleanup = this._introGalleryRegistry.get(g);
        if (cleanup) {
          try { cleanup(); } catch (e) {}
          this._introGalleryRegistry.delete(g);
        }
      });
    }
  
    setupSupplierLogos() {
      const logoWrappers = Array.from(this.section.querySelectorAll('[data-supplier-logos]'));
      logoWrappers.forEach((wrapper) => {
        const logos = Array.from(wrapper.querySelectorAll('[data-logo-item]'));
        if (logos.length <= 1) return;
        let current = 0;
        let timer = null;
        const interval = 2000;
  
        const cycle = () => {
          logos.forEach((logo, index) => {
            logo.classList.toggle('is-active', index === current);
          });
          current = (current + 1) % logos.length;
        };
  
        const start = () => {
          if (timer) return;
          cycle();
          timer = window.setInterval(cycle, interval);
        };
  
        const stop = () => {
          if (timer) {
            window.clearInterval(timer);
            timer = null;
          }
        };
  
        start();
        wrapper.addEventListener('mouseenter', stop);
        wrapper.addEventListener('mouseleave', () => {
          stop();
          current = 0;
          start();
        });
      });
    }
  
    destroy() {
      window.removeEventListener('resize', this.handleResize);
      if (typeof this.mediaQuery.removeEventListener === 'function') {
        this.mediaQuery.removeEventListener('change', this.handleResize);
      }
      this._clearTransformTransitionCleanup();
      if (this._hoverPauseTimer) {
        clearTimeout(this._hoverPauseTimer);
        this._hoverPauseTimer = null;
      }
      if (this.sliderContainer) {
        this.sliderContainer.removeEventListener('mouseenter', this._onSliderMouseEnter);
        this.sliderContainer.removeEventListener('mouseleave', this._onSliderMouseLeave);
      }
      if (this._visibilityObserver) {
        this._visibilityObserver.disconnect();
        this._visibilityObserver = null;
      }
      if (this._reducedMotionQuery && this._onReducedMotionChange) {
        this._reducedMotionQuery.removeEventListener('change', this._onReducedMotionChange);
      }
      this.pauseAutoplay();
  
      // 清理所有介绍画廊的计时器
      if (this._introGalleryRegistry && this._introGalleryRegistry.size) {
        try {
          this._introGalleryRegistry.forEach((cleanup) => {
            if (typeof cleanup === 'function') {
              try { cleanup(); } catch (e) {}
            }
          });
        } finally {
          this._introGalleryRegistry.clear();
        }
      }
    }
  }
  
  function initBrandHighlights(root = document) {
    const sections = root.querySelectorAll('[id^="BrandHighlights-"][data-section-id]');
    sections.forEach((section) => {
      if (section.__brandHighlights) {
        section.__brandHighlights.destroy();
      }
      section.__brandHighlights = new BrandHighlightsSection(section);
    });
  }
  
  function unloadBrandHighlights(root = document) {
    const sections = root.querySelectorAll('[id^="BrandHighlights-"][data-section-id]');
    sections.forEach((section) => {
      if (section.__brandHighlights) {
        section.__brandHighlights.destroy();
        delete section.__brandHighlights;
      }
    });
  }
  
  // 懒初始化：仅在 section 即将进入视口时才执行 new BrandHighlightsSection()
  // 提前量 300px 确保用户滚动到此处前已完成初始化
  function lazyInitBrandHighlights(root = document) {
    const sections = Array.from(root.querySelectorAll('[id^="BrandHighlights-"][data-section-id]'));
    if (!sections.length) return;

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        const section = entry.target;
        if (section.__brandHighlights) {
          section.__brandHighlights.destroy();
        }
        section.__brandHighlights = new BrandHighlightsSection(section);
      });
    }, { rootMargin: '300px 0px', threshold: 0 });

    sections.forEach(section => {
      if (!section.__brandHighlights) {
        observer.observe(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => lazyInitBrandHighlights());
  } else {
    lazyInitBrandHighlights();
  }

  // Shopify 编辑器：直接立即初始化（不走懒加载）
  document.addEventListener('shopify:section:load', (event) => {
    initBrandHighlights(event.target);
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    unloadBrandHighlights(event.target);
  });
  